import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Stack,
  CircularProgress,
  Grid,
  Paper
} from '@mui/material';
import { PieChart as PieChartIcon } from '@mui/icons-material';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Transaction } from '../../types';
import { processCategoryData, formatCurrency, formatPercentage, CategoryData } from '../../utils/chartUtils';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

interface CategoryBreakdownChartProps {
  transactions: Transaction[];
  loading?: boolean;
  transactionType?: 'expense' | 'income' | 'savings';
}

const CategoryBreakdownChart: React.FC<CategoryBreakdownChartProps> = ({
  transactions,
  loading = false,
  transactionType = 'expense'
}) => {
  const theme = useTheme();

  const categoryData = useMemo(() => {
    return processCategoryData(transactions, transactionType);
  }, [transactions, transactionType]);

  const chartData = useMemo(() => {
    if (categoryData.length === 0) {
      return {
        labels: ['No Data'],
        datasets: [
          {
            data: [1],
            backgroundColor: [alpha(theme.palette.text.disabled, 0.1)],
            borderColor: [theme.palette.divider],
            borderWidth: 1
          }
        ]
      };
    }

    return {
      labels: categoryData.map((cat: CategoryData) => cat.name),
      datasets: [
        {
          data: categoryData.map((cat: CategoryData) => cat.amount),
          backgroundColor: categoryData.map((cat: CategoryData) => cat.color),
          borderColor: categoryData.map((cat: CategoryData) => alpha(cat.color, 0.8)),
          borderWidth: 2,
          hoverBackgroundColor: categoryData.map((cat: CategoryData) => alpha(cat.color, 0.8)),
          hoverBorderColor: categoryData.map((cat: CategoryData) => cat.color),
          hoverBorderWidth: 3
        }
      ]
    };
  }, [categoryData, theme]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // We'll create a custom legend
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context: any) => {
            const category = categoryData[context.dataIndex];
            if (!category) return '';
            return [
              `Amount: ${formatCurrency(category.amount)}`,
              `Percentage: ${formatPercentage(category.percentage)}`,
              `Transactions: ${category.transactionCount}`
            ];
          }
        }
      }
    },
    cutout: '60%', // Makes it a doughnut chart
    animation: {
      animateScale: true,
      animateRotate: true
    }
  }), [theme, categoryData]);

  const totalAmount = useMemo(() => {
    return categoryData.reduce((sum: number, cat: CategoryData) => sum + cat.amount, 0);
  }, [categoryData]);

  const totalTransactions = useMemo(() => {
    return categoryData.reduce((sum: number, cat: CategoryData) => sum + cat.transactionCount, 0);
  }, [categoryData]);

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.4)})`,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="600">
          Category Breakdown
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {transactionType === 'expense' ? 'Expense' : transactionType === 'income' ? 'Income' : 'Savings'} distribution by category
        </Typography>

        {/* Summary Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                background: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Total {transactionType === 'expense' ? 'Expenses' : transactionType === 'income' ? 'Income' : 'Savings'}
              </Typography>
              <Typography variant="h6" fontWeight="600" color="primary.main">
                {formatCurrency(totalAmount)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                background: alpha(theme.palette.secondary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Categories
              </Typography>
              <Typography variant="h6" fontWeight="600" color="secondary.main">
                {categoryData.length}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {categoryData.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="300px"
            color="text.secondary"
          >
            <PieChartIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
            <Typography variant="body1" color="text.secondary">
              No {transactionType} data available
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Chart */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '300px', position: 'relative' }}>
                <Doughnut data={chartData} options={chartOptions} />
                {/* Center text */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h6" fontWeight="600" color="text.primary">
                    {formatCurrency(totalAmount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {totalTransactions} transactions
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Category List */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '300px', overflow: 'auto' }}>
                <List dense>
                  {categoryData.slice(0, 8).map((category: CategoryData, index: number) => (
                    <ListItem
                      key={category.name}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: alpha(category.color, 0.05)
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            backgroundColor: category.color,
                            width: 32,
                            height: 32,
                            fontSize: '0.875rem'
                          }}
                        >
                          {category.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="500">
                            {category.name}
                          </Typography>
                        }
                        secondary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={formatCurrency(category.amount)}
                              size="small"
                              variant="outlined"
                              sx={{
                                backgroundColor: alpha(category.color, 0.1),
                                borderColor: alpha(category.color, 0.3),
                                color: category.color,
                                fontSize: '0.75rem'
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatPercentage(category.percentage)}
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Top Categories Summary */}
        {categoryData.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Top {transactionType === 'expense' ? 'Expense' : transactionType === 'income' ? 'Income' : 'Savings'} Categories
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {categoryData.slice(0, 3).map((category: CategoryData) => (
                <Chip
                  key={category.name}
                  label={`${category.name} (${formatPercentage(category.percentage)})`}
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: alpha(category.color, 0.1),
                    borderColor: alpha(category.color, 0.3),
                    color: category.color,
                    '& .MuiChip-label': {
                      fontWeight: 500
                    }
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CategoryBreakdownChart; 